import { useNavigator } from '@repo/toolkit/use-navigator'
import ActionButton from '@repo/ui/action-button'
import { Column } from '@repo/ui/data-table'
import { DuplicateIcon, FamilyThreeIcon, PlusIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { API, constructApiUrl } from '../../../../api/apis.config'
import { createOlHostFamilyPrice } from '../../../../api/ols/host-family-prices/create-ol-host-family-price'
import { Paths } from '../../../../lib/constants/path'
import { useFilters } from '../../../../lib/hooks/use-filters'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../lib/hooks/use-translate'
import { CurrencyEnum } from '../../../../lib/types/enums/common/currency.enum'
import {
  OLHostFamilyPricePerNight,
  OLHostFamilyPricePerNightFilters
} from '../../../../lib/types/models/ol/host-family-price-per-night/ol-host-family-price-per-night'
import { PeriodProperties } from '../../../../lib/types/models/period/period.properties'
import { twoButtonsConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformOlHostFamilyPriceFiltersToUrl,
  transformUrlToOlHostFamilyPriceFilters
} from '../../../../lib/utils/filters-mapping/ol-host-family-price'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { getHostFamilyPriceColumns } from '../../../../views/ol-personal-space/fees/utils'
import { constructOlPath } from '../../../../views/ols/views/utils'
import FilterDrawer from '../../../elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../elements/dynamic-table'
import { countFilters } from '../../../elements/dynamic-table/utils/countFilters'
import withPeriods, { WithPeriodsProps } from '../../../hoc/with-periods'
import { olHostFamilyPriceToOlHostFamilyPriceInput } from './ol-host-family-price.utils'
import OlHostFamilyPriceForm from './ol-host-family-price-form'
import { OlHostFamilyPricesFilter } from './ol-host-family-prices-filter'

type Props = WithPeriodsProps & {
  olId: number
}

const OlHostFamilyPrices: React.FC<Props> = ({ periods, olId }) => {
  const tableRef = React.useRef<DynamicTableRef<OLHostFamilyPricePerNight>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { navigateWithEvent, navigate } = useNavigator()
  const { t } = useTranslate()

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'filters' | 'duplicate',
    OLHostFamilyPricePerNight
  >(['filters', 'duplicate'])

  const { selectedFilter, filter, onFilterChange, onSelectedFilterChange } =
    useFilters<OLHostFamilyPricePerNightFilters>({
      initialFilters: {
        products: [],
        year: new Date().getFullYear(),
        periods: PeriodProperties.getCurrentYearPeriods(periods ?? []).map((period) => period.name)
      },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformOlHostFamilyPriceFiltersToUrl,
      transformUrlToFilter: transformUrlToOlHostFamilyPriceFilters
    })

  const hostFamilyPriceColumns: Column<OLHostFamilyPricePerNight>[] = [
    ...getHostFamilyPriceColumns('HOST_FAMILY', t),
    {
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => (
        <ActionButton
          variant="primary"
          icon={DuplicateIcon}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            toggleModal('duplicate', row.original)
          }}
        />
      )
    }
  ]

  const createOlHostFamilyPriceMutation = useMutation({
    mutationFn: createOlHostFamilyPrice,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Prix Famille-hôtesse dupliqué avec succès')
      tableRef.current?.refetch()
      closeModel('duplicate')
    }
  })

  return (
    <InnerBox className="rounded-2xl p-0">
      <DynamicDataTable<OLHostFamilyPricePerNight, OLHostFamilyPricePerNightFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title={'Rémunération Famille-hôtesse'}
        deleteMessage={
          'Êtes-vous sûr de vouloir supprimer cette rémunération de la famille-hôtesse ? '
        }
        columns={hostFamilyPriceColumns}
        selectable
        allowSearchParam
        isSearchable={false}
        version="FULL"
        onRowClick={(olHostFamilyPrice, event) => {
          navigateWithEvent(
            constructOlPath(
              Paths.OL_DETAILS_UPDATE_HOST_FAMILY_PRICES_PAGE.replace(
                ':hostFamilyPriceId',
                olHostFamilyPrice.id.toString()
              ),
              olId
            ),
            event
          )
        }}
        endpoint={constructApiUrl(API.ols.getOlHostFamilyPrices.url, { olId })}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Ajouter un tarif Famille-Hôtesse',
            fn: () => {
              navigate(constructOlPath(Paths.OL_DETAILS_CREATE_HOST_FAMILY_PRICES_PAGE, olId))
            }
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onReset={() => {
          onSelectedFilterChange(undefined)
          closeModel('filters')
          tableRef.current?.refetch()
        }}
        onFilterChange={onFilterChange}
        onClose={() => closeModel('filters')}
        onApply={() => {
          onSelectedFilterChange({ ...filter })
          closeModel('filters')
          tableRef.current?.refetch()
        }}
      >
        <OlHostFamilyPricesFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {selectedData && (
        <>
          <Modal
            variant="regular"
            icon={FamilyThreeIcon}
            isOpen={modalState.duplicate}
            onCancel={() => closeModel('duplicate')}
            title="Dupliquer cette rémunération famille-hôtesse"
            isLoading={createOlHostFamilyPriceMutation.isPending}
            size="medium"
            className="p-0"
            hideActions
          >
            <OlHostFamilyPriceForm
              mode="duplicate"
              olId={olId}
              innerBoxClassName="border-none p-0"
              className="p-5"
              defaultValues={olHostFamilyPriceToOlHostFamilyPriceInput(selectedData, {
                currency: CurrencyEnum.EUR
              })}
              buttonsConfig={twoButtonsConfig({
                confirmText: 'Dupliquer',
                cancelText: 'Fermer',
                onConfirm: async (values) => {
                  const olHostFamily = await createOlHostFamilyPriceMutation.mutateAsync({
                    id: olId,
                    ...values
                  })
                  navigate(
                    constructOlPath(
                      Paths.OL_DETAILS_UPDATE_HOST_FAMILY_PRICES_PAGE.replace(
                        ':hostFamilyPriceId',
                        olHostFamily.id.toString()
                      ),
                      olId
                    ),
                    { replace: true }
                  )
                },
                onCancel: () => closeModel('duplicate')
              })}
            />
          </Modal>
        </>
      )}
    </InnerBox>
  )
}

export default withPeriods(OlHostFamilyPrices)
